import type { FiltrosDaLista } from '../../../api/filtrosDeGmud'
import type { OrigemGmud, ResumoGmud, StatusGmud } from '../../../types/gmud'
import { ETAPAS, etapaAtual, type EtapaDoProcesso } from '../detalhe/etapasDoAnexoXxi'
import { zonaAceitaSoltar } from './arraste'
import { QUADRO } from './textosDoQuadro'

/**
 * As colunas do quadro, DERIVADAS das sete etapas do ANEXO XXI — nunca transcritas de novo.
 *
 * `ETAPAS` (em `detalhe/etapasDoAnexoXxi.ts`) já é a transcrição do anexo, título, resumo e
 * responsável inclusive. Reescrever isso aqui produziria duas transcrições do mesmo documento
 * contratual, e elas divergiriam na primeira correção de redação — com o quadro afirmando uma
 * atribuição de responsabilidade que o detalhe da solicitação nega.
 */

/**
 * O teto que o servidor impõe (`GmudConsultaService.TAMANHO_MAXIMO_PAGINA`).
 *
 * Pedir mais não adianta: o backend faz `Math.min(tamanho, 100)`. O quadro pede o teto e, quando o
 * recorte tiver mais do que isso, diz na tela quantas está mostrando de quantas. Paginar um quadro
 * hoje seria construir o defeito e esperar — com `origem=V2` o recorte inteiro cabe numa página.
 */
export const TAMANHO_DO_QUADRO = 100

/**
 * O quadro é só do processo deste sistema, e o recorte não é herdado de `ORIGEM_PADRAO`.
 *
 * O mesmo valor, por outra razão: na lista, `['V2']` é o que se mostra quando ninguém pede
 * procedência, e quem quiser o histórico pede. Aqui é o ÚNICO recorte possível, por dois motivos
 * independentes — conformidade (1.027 cartões escritos "Aprovado" afirmariam decisão de comitê que
 * nunca houve) e mecânica (`MIGRADO_V1` tem `etapaAtual` nulo e nenhuma linha de saída na matriz:
 * seriam 1.027 cartões imóveis para sempre). Se um dia o padrão da lista mudar, o quadro não segue.
 */
export const ORIGEM_DO_QUADRO: OrigemGmud[] = ['V2']

/**
 * Todo estado que existe, escrito uma vez.
 *
 * O `Record<StatusGmud, true>` é o portão: um estado novo no domínio quebra o build aqui, em vez de
 * sumir do quadro em silêncio — que é o modo como um estado novo passaria despercebido, já que o
 * quadro continuaria desenhando as sete colunas normalmente.
 */
const PRESENCA_DE_TODO_STATUS: Record<StatusGmud, true> = {
  RASCUNHO: true,
  SOLICITADA: true,
  EM_AVALIACAO_CAB: true,
  AGUARDANDO_AJUSTES: true,
  APROVADA: true,
  RECUSADA: true,
  EM_EXECUCAO: true,
  EM_VERIFICACAO: true,
  CONCLUIDA: true,
  REVERTIDA_ROLLBACK: true,
  CANCELADA: true,
  MIGRADO_V1: true,
}

const TODOS_OS_STATUS = Object.keys(PRESENCA_DE_TODO_STATUS) as StatusGmud[]

/** Os estados que estão em alguma das sete etapas — os que o quadro desenha em coluna. */
export const STATUS_EM_FLUXO: StatusGmud[] = TODOS_OS_STATUS.filter((status) => etapaAtual(status) !== null)

/**
 * Os terminais: recusada, cancelada e revertida.
 *
 * Viram rodapé com contagem, e não uma oitava coluna: coluna de terminal só cresce, e ocupa em
 * largura o espaço das etapas onde o trabalho de fato está parado. `MIGRADO_V1` fica fora daqui
 * também — ele não é desfecho deste processo, é registro que nunca entrou nele.
 */
export const STATUS_FORA_DO_FLUXO: StatusGmud[] = TODOS_OS_STATUS.filter(
  (status) => etapaAtual(status) === null && status !== 'MIGRADO_V1',
)

/** Uma faixa nomeada dentro da coluna. Coluna com uma zona só não mostra rótulo de zona nenhum. */
export interface ZonaDoQuadro {
  status: StatusGmud
  /** Nulo quando a coluna tem uma zona só: ali o título da coluna já diz tudo. */
  rotulo: string | null
  ajuda: string | null
  aceitaSoltar: boolean
}

export interface ColunaDoQuadro {
  etapa: EtapaDoProcesso
  zonas: ZonaDoQuadro[]
}

/**
 * As duas zonas nomeadas da etapa 4, e a razão de a etapa se partir.
 *
 * `AGUARDANDO_AJUSTES` e `APROVADA` caem os dois na etapa 4 do anexo ("Aprovação do plano") e são
 * **notícias opostas**: "o comitê devolveu pedindo correção" cobra a contratada; "o comitê liberou
 * e a execução não começou" cobra a contratada por outra coisa — e no meio delas está a decisão
 * mais cara do sistema. Uma coluna só, com os dois montes juntos, esconderia a diferença.
 */
const ZONAS_NOMEADAS: Partial<Record<StatusGmud, { rotulo: string; ajuda: string }>> = {
  AGUARDANDO_AJUSTES: QUADRO.zonas.aguardandoAjustes,
  APROVADA: QUADRO.zonas.aprovada,
}

export const COLUNAS_DO_QUADRO: ColunaDoQuadro[] = ETAPAS.map((etapa) => {
  const status = STATUS_EM_FLUXO.filter((candidato) => etapaAtual(candidato) === etapa.numero)
  return {
    etapa,
    zonas: status.map((estado) => ({
      status: estado,
      rotulo: status.length > 1 ? (ZONAS_NOMEADAS[estado]?.rotulo ?? null) : null,
      ajuda: status.length > 1 ? (ZONAS_NOMEADAS[estado]?.ajuda ?? null) : null,
      aceitaSoltar: zonaAceitaSoltar(estado),
    })),
  }
})

/**
 * O recorte que o quadro consulta, no formato da listagem.
 *
 * @param status os estados desta consulta — em fluxo, ou fora dele
 * @returns os critérios para `GET /gmuds`
 */
export function recorteDoQuadro(status: StatusGmud[]): FiltrosDaLista {
  return {
    status,
    prioridade: [],
    origem: ORIGEM_DO_QUADRO,
    chamado: '',
    q: '',
    pagina: 1,
    tamanho: TAMANHO_DO_QUADRO,
  }
}

/**
 * Os cartões separados por estado, mantendo a ordem em que o servidor os devolveu.
 *
 * A ordem do quadro é temporal e vem da consulta; não há ordenação manual a guardar. Foi por isso
 * que `@dnd-kit/sortable` não tinha cenário nenhum neste sistema.
 *
 * @param itens a página de solicitações
 * @returns cada estado com os seus cartões
 */
export function agruparPorStatus(itens: ResumoGmud[]): Map<StatusGmud, ResumoGmud[]> {
  const porStatus = new Map<StatusGmud, ResumoGmud[]>()
  for (const item of itens) {
    const jaVistos = porStatus.get(item.status) ?? []
    jaVistos.push(item)
    porStatus.set(item.status, jaVistos)
  }
  return porStatus
}

/**
 * Quantos cartões a coluna inteira tem, somando as zonas.
 *
 * @param porStatus o agrupamento
 * @param coluna a coluna
 * @returns a contagem
 */
export function quantasNaColuna(porStatus: Map<StatusGmud, ResumoGmud[]>, coluna: ColunaDoQuadro): number {
  return coluna.zonas.reduce((soma, zona) => soma + (porStatus.get(zona.status)?.length ?? 0), 0)
}

/**
 * O identificador do elemento da coluna, para a barra de salto mover o foco até ele.
 *
 * @param numero o número da etapa
 * @returns o `id` do elemento
 */
export function idDaColuna(numero: number): string {
  return `quadro-etapa-${numero}`
}
